import { format, startOfYear, subDays } from 'date-fns';
import type { FilterSelectOption } from '@/components/ui/FilterSelect';
import {
  SUPPORT_TICKET_PRIORITY_OPTIONS,
  SUPPORT_TICKET_STATUS_OPTIONS,
  SUPPORT_TICKET_TYPE_OPTIONS,
} from '@/components/support/SupportTicketShared';
import type { SupportTicketListParams } from '@/services/supportTickets.service';

/**
 * Filter and sort choices for the Help Center ticket list. Every default is
 * `''` because FilterSelect highlights any other value as an active filter.
 */
export interface HelpTicketListState {
  status: string;
  type: string;
  priority: string;
  opened: string;
  sort: string;
}

export const DEFAULT_HELP_TICKET_LIST_STATE: HelpTicketListState = {
  status: '',
  type: '',
  priority: '',
  opened: '',
  sort: '',
};

export const HELP_TICKET_STATUS_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'All statuses' },
  ...SUPPORT_TICKET_STATUS_OPTIONS,
];

export const HELP_TICKET_TYPE_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'All types' },
  ...SUPPORT_TICKET_TYPE_OPTIONS,
];

export const HELP_TICKET_PRIORITY_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'All priorities' },
  ...SUPPORT_TICKET_PRIORITY_OPTIONS,
];

// The option text carries the control's purpose: nothing else on screen names
// these two dropdowns.
export const HELP_TICKET_OPENED_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'Opened: Any time' },
  { value: '7d', label: 'Opened: Last 7 days' },
  { value: '30d', label: 'Opened: Last 30 days' },
  { value: '90d', label: 'Opened: Last 90 days' },
  { value: 'year', label: 'Opened: This year' },
];

export const HELP_TICKET_SORT_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'Sort: Latest activity' },
  { value: 'opened_desc', label: 'Sort: Newest first' },
  { value: 'opened_asc', label: 'Sort: Oldest first' },
  { value: 'priority', label: 'Sort: Highest priority' },
];

const OPTIONS_BY_FIELD: Record<keyof HelpTicketListState, FilterSelectOption[]> = {
  status: HELP_TICKET_STATUS_OPTIONS,
  type: HELP_TICKET_TYPE_OPTIONS,
  priority: HELP_TICKET_PRIORITY_OPTIONS,
  opened: HELP_TICKET_OPENED_OPTIONS,
  sort: HELP_TICKET_SORT_OPTIONS,
};

const SORT_PARAMS: Record<string, Pick<SupportTicketListParams, 'sortBy' | 'sortDir'>> = {
  opened_desc: { sortBy: 'opened', sortDir: 'desc' },
  opened_asc: { sortBy: 'opened', sortDir: 'asc' },
  priority: { sortBy: 'priority', sortDir: 'desc' },
};

const OPENED_DAYS: Record<string, number> = { '7d': 7, '30d': 30, '90d': 90 };

/** Per user and account, so people sharing a front desk browser keep their own choices. */
export function helpTicketListStorageKey(userId: number | undefined, accountId: number | undefined): string {
  return `ftp-help-ticket-list:${userId ?? 0}:${accountId ?? 0}`;
}

/** Any stored field that is not a current option falls back to its default. */
export function sanitizeHelpTicketListState(raw: unknown): HelpTicketListState {
  const stored = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const next = { ...DEFAULT_HELP_TICKET_LIST_STATE };
  (Object.keys(OPTIONS_BY_FIELD) as Array<keyof HelpTicketListState>).forEach((field) => {
    const value = stored[field];
    if (typeof value === 'string' && OPTIONS_BY_FIELD[field].some((option) => option.value === value)) {
      next[field] = value;
    }
  });
  return next;
}

/** Status, type, priority or opened narrowed. Sort reorders and never empties the list. */
export function hasActiveHelpTicketFilters(state: HelpTicketListState): boolean {
  return state.status !== '' || state.type !== '' || state.priority !== '' || state.opened !== '';
}

/**
 * The first calendar day (the user's own calendar) an Opened preset covers,
 * as `Y-m-d`, or undefined for Any time. "Last 7 days" is 7 days counting
 * today, so it starts 6 days back.
 */
export function createdFromForOpened(opened: string, now: Date = new Date()): string | undefined {
  if (opened === 'year') return format(startOfYear(now), 'yyyy-MM-dd');
  const days = OPENED_DAYS[opened];
  return days ? format(subDays(now, days - 1), 'yyyy-MM-dd') : undefined;
}

/** The list request's filter and sort params. Defaults send nothing, as before. */
export function helpTicketListParams(
  state: HelpTicketListState,
  now: Date = new Date(),
): Pick<SupportTicketListParams, 'status' | 'type' | 'priority' | 'createdFrom' | 'sortBy' | 'sortDir'> {
  return {
    ...(state.status ? { status: state.status } : {}),
    ...(state.type ? { type: state.type } : {}),
    ...(state.priority ? { priority: state.priority } : {}),
    ...(state.opened ? { createdFrom: createdFromForOpened(state.opened, now) } : {}),
    ...(SORT_PARAMS[state.sort] ?? {}),
  };
}
